import { addDays, format, isToday, startOfDay } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { TimeDropdown } from "@/components/ui/time-dropdown";
import { cn, getDateWithTime } from "@/lib/utils";

import { AddSessionFormData } from "../types/add-session-schema";

type SessionDetailsElementProps = {
  isDescriptionRequired?: boolean;
};

export function SessionDetailsElement({
  isDescriptionRequired = false,
}: SessionDetailsElementProps) {
  const form = useFormContext<AddSessionFormData>();

  const startDate = form.watch("startDate");

  const minStartTime = isToday(startDate) ? new Date() : startOfDay(startDate);
  const startTime = form.watch("startTime");

  const minEndTime = getDateWithTime(startDate, startTime);

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Title</FormLabel>
            <FormControl>
              <Input {...field} placeholder="My Call" />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired={isDescriptionRequired}>
              Description
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Enter a description..." />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="flex flex-col gap-4 xl:flex-row">
        <FormField
          control={form.control}
          name="startDate"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel isRequired className="self-start">
                Session Date
              </FormLabel>
              <Popover>
                <PopoverTrigger asChild>
                  <FormControl>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-[240px] pl-3 text-left font-normal",
                        !field.value && "text-muted-foreground",
                      )}
                      disabled={form.formState.isSubmitting}
                    >
                      {field.value ? (
                        format(field.value, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                      <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={field.value}
                    onSelect={field.onChange}
                    disabled={(date) => date < addDays(new Date(), -1)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-row gap-4">
          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel isRequired>From</FormLabel>

                <FormControl>
                  <TimeDropdown
                    value={field.value}
                    onChange={field.onChange}
                    disabled={form.formState.isSubmitting}
                    minDate={minStartTime}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel isRequired>To</FormLabel>

                <FormControl>
                  <TimeDropdown
                    value={field.value}
                    onChange={field.onChange}
                    disabled={form.formState.isSubmitting}
                    minDate={minEndTime}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
